import type { Ref } from 'vue'

import { useToast } from '@shared/ui/toast'

import { DisciplineMessageSender, DisciplinePaymentLinksTab } from './enums'
import { useDisciplinePaymentLinksStore } from './payment-links/store'
import { useDisciplineStore } from './store'
import { useDisciplinePrivileges } from './discipline-privileges.hook'
import { useDisciplineEnrollSendApi, useOrderSendApi } from '../api/payment-send.hook'
import { MESSAGE_SENDER_LABELS, summarizeDeliveries } from './discipline-payment-links'

interface DisciplinePaymentSenderOption {
  sender: DisciplineMessageSender
  label: string
}

interface DisciplinePaymentShareReturn {
  refIsPricingTab: ComputedRef<boolean>
  refLink: ComputedRef<string>
  refIsSending: Ref<boolean>
  refCanSend: ComputedRef<boolean>
  canManageOrders: ComputedRef<boolean>
  senderOptions: readonly DisciplinePaymentSenderOption[]
  setTab: (tab: DisciplinePaymentLinksTab) => void
  isSenderChecked: (sender: DisciplineMessageSender) => boolean
  toggleSender: (sender: DisciplineMessageSender) => void
  send: () => Promise<void>
}

const SENDER_OPTIONS: readonly DisciplinePaymentSenderOption[] = [
  DisciplineMessageSender.Email,
  DisciplineMessageSender.Sms,
  DisciplineMessageSender.Whatsapp,
].map(sender => ({ sender, label: MESSAGE_SENDER_LABELS[sender] }))

export const useDisciplinePaymentShare = (): DisciplinePaymentShareReturn => {
  const route = useRoute()
  const toast = useToast()
  const store = useDisciplinePaymentLinksStore()
  const disciplineStore = useDisciplineStore()
  const { canManageOrders } = useDisciplinePrivileges()

  const refIsSending = ref<boolean>(false)

  const refIsPricingTab = computed<boolean>(() => store.isPricingPageTab)
  const refLink = computed<string>(() => refIsPricingTab.value
    ? disciplineStore.getDetails?.links?.pricing_link ?? ''
    : store.getCurrentLink?.link ?? '')

  const refCanSend = computed<boolean>(() =>
    canManageOrders.value && !!refLink.value && store.getSenders.length > 0 && !refIsSending.value)

  const setTab = (tab: DisciplinePaymentLinksTab): void => {
    store.setLinksTab(tab)
  }

  const isSenderChecked = (sender: DisciplineMessageSender): boolean => store.getSenders.includes(sender)

  const toggleSender = (sender: DisciplineMessageSender): void => {
    store.setSender(sender, !isSenderChecked(sender))
  }

  const send = async (): Promise<void> => {
    if (!refCanSend.value) return

    const senders = [...store.getSenders]
    const request = {
      senders,
      onStart: () => {
        refIsSending.value = true
      },
      onFinish: () => {
        refIsSending.value = false
      },
    }

    const code = disciplineStore.getDetails?.code
    const orderId = store.getCurrentLink?.orderId
    const results = refIsPricingTab.value
      ? code ? await useDisciplineEnrollSendApi({ ...request, disciplineId: route.params.id as string, code }) : null
      : orderId ? await useOrderSendApi({ ...request, orderId }) : null

    if (!results) return

    const { delivered, problems } = summarizeDeliveries(results)
    if (delivered.length) toast.success(`Link was sent via ${delivered.join(', ')}.`)
    if (problems.length) toast.warning(`Link was not sent via ${problems.join('; ')}.`)
  }

  return {
    refIsPricingTab,
    refLink,
    refIsSending,
    refCanSend,
    canManageOrders,
    senderOptions: SENDER_OPTIONS,
    setTab,
    isSenderChecked,
    toggleSender,
    send,
  }
}
